import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { UpdateUserPasswordValidator } from "../../../../utils/validators/auth";
import {
  EyeIcon,
  FieldContainer,
  FieldInput,
  FieldInputContainer,
  FieldLabel,
  FieldLabelContainer,
  FormContainer,
  MainFieldsContainer,
  UpdateButtonContainer,
  UpdatePasswordWrapper,
} from "./updatePassword.style";
import eye_icon from "../../../../assets/images/exe_icon.svg";
import useAuth from "../../../Auth/hooks/useAuth";
import {
  numberReg,
  specialCharsReg,
  upperCaseReg,
} from "../../../../shared/sharedConstants";
import { toast } from "react-toastify";
import FormErrors from "../../../../components/FormErrors";
import { UtilityBtn } from "../Profile/profile/profile.style";

const UpdatePassword = () => {
  interface IFormValues {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordOptions, setPasswordOptions] = useState({
    isUpperCase: false,
    isNumber: false,
    isSpecialChars: false,
    isEmpty: false,
    isEightChars: false,
  });

  // Not doChangeLoggedInUserPassword: that checks the old password against Firebase,
  // but the current password now lives only in the .NET identity store.
  const { changeDotNetPassword } = useAuth();

  const { isLoading, mutateAsync: doCreatePassword } = changeDotNetPassword;

  const initialValues: IFormValues = {
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  };
  const submitHandler = async (values: IFormValues) => {
    try {
      const response = await doCreatePassword({
        currentPassword: values.oldPassword,
        newPassword: values.newPassword,
      });

      toast.success(
        response?.data?.message || "Your password has been changed.",
        {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 8000,
          toastId: "1",
        }
      );
      resetForm();
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error("Password change failed", error?.response ?? error);

      // A 401 means a stale session, not a wrong password.
      const status = error.response?.status;

      const text =
        status === 401
          ? "Your session has expired. Please sign in again and retry."
          : error.response?.data?.error ||
            error.response?.data?.message ||
            "Something went wrong, try again";

      toast.error(text, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 8000,
        toastId: "1",
      });
    }
  };

  const {
    handleSubmit,
    values,
    handleChange,
    handleBlur,
    errors,
    resetForm,
    dirty,
  } = useFormik({
    initialValues: initialValues,
    validationSchema: UpdateUserPasswordValidator,
    onSubmit: submitHandler,
  });

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const toggleOldPassword = () => {
    setShowOldPassword(!showOldPassword);
  };

  useEffect(() => {
    upperCaseReg.test(values.newPassword)
      ? setPasswordOptions((prevState) => ({
          ...prevState,
          isUpperCase: true,
        }))
      : setPasswordOptions((prevState) => ({
          ...prevState,
          isUpperCase: false,
        }));

    specialCharsReg.test(values.newPassword)
      ? setPasswordOptions((prevState) => ({
          ...prevState,
          isSpecialChars: true,
        }))
      : setPasswordOptions((prevState) => ({
          ...prevState,
          isSpecialChars: false,
        }));

    numberReg.test(values.newPassword)
      ? setPasswordOptions((prevState) => ({
          ...prevState,
          isNumber: true,
        }))
      : setPasswordOptions((prevState) => ({
          ...prevState,
          isNumber: false,
        }));

    values.newPassword !== ""
      ? setPasswordOptions((prevState) => ({ ...prevState, isEmpty: true }))
      : setPasswordOptions((prevState) => ({
          ...prevState,
          isEmpty: false,
        }));

    values.newPassword.length >= 8
      ? setPasswordOptions((prevState) => ({
          ...prevState,
          isEightChars: true,
        }))
      : setPasswordOptions((prevState) => ({
          ...prevState,
          isEightChars: false,
        }));
  }, [values.newPassword]);

  return (
    <UpdatePasswordWrapper>
      <FormContainer>
        <form onSubmit={handleSubmit}>
          <MainFieldsContainer>
            <div style={{ display: "block" }}>
              <FieldContainer>
                <FieldLabelContainer>
                  <FieldLabel htmlFor="oldPassword">Old Password</FieldLabel>
                </FieldLabelContainer>
                <FieldInputContainer>
                  <FieldInput
                    type={showOldPassword ? "text" : "password"}
                    name="oldPassword"
                    id="oldPassword"
                    value={values.oldPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <EyeIcon
                    src={eye_icon}
                    alt="eye icon"
                    onClick={toggleOldPassword}
                  />
                </FieldInputContainer>
              </FieldContainer>
              <FieldContainer>
                <FieldLabelContainer>
                  <FieldLabel htmlFor="newPassword">New Password</FieldLabel>
                </FieldLabelContainer>
                <FieldInputContainer>
                  <FieldInput
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    id="newPassword"
                    value={values.newPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <EyeIcon
                    src={eye_icon}
                    alt="eye icon"
                    onClick={togglePassword}
                  />
                </FieldInputContainer>
              </FieldContainer>
              <FieldContainer>
                <FieldLabelContainer>
                  <FieldLabel htmlFor="confirmPassword">
                    Re-enter New Password
                  </FieldLabel>
                </FieldLabelContainer>
                <FieldInputContainer>
                  <FieldInput
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    id="confirmPassword"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {/* TODO:make eye icon cross */}

                  <EyeIcon
                    src={eye_icon}
                    alt="eye icon"
                    onClick={toggleConfirmPassword}
                  />
                </FieldInputContainer>
              </FieldContainer>
            </div>
            {dirty && (
              <FormErrors
                empty={passwordOptions.isEmpty}
                upperCase={passwordOptions.isUpperCase}
                number={passwordOptions.isNumber}
                specialChars={passwordOptions.isSpecialChars}
                eightChars={passwordOptions.isEightChars}
                values={values}
                errors={errors}
                showMatch
                pwToCheck={"confirmPassword"}
              />
            )}
          </MainFieldsContainer>
          <UpdateButtonContainer>
            <UtilityBtn buttonText="Update" type="submit" disabled={isLoading}>
              {isLoading ? "Updating" : "Update"}
            </UtilityBtn>
          </UpdateButtonContainer>
        </form>
      </FormContainer>
    </UpdatePasswordWrapper>
  );
};

export default UpdatePassword;
